import React from 'react';
import { FiCheck, FiX, FiSearch, FiTrash2, FiPlus } from 'react-icons/fi';
import config from '../config';

export default function MoodleBadgeEditor({ 
  isOpen, 
  onClose, 
  onSave,
  initialData = { id: '', name: '', description: '', imageUrl: '', link: '', badges: [] }
}) {
  const [badges, setBadges] = React.useState([]);
  const [filteredBadges, setFilteredBadges] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedBadges, setSelectedBadges] = React.useState([]);
  const [link, setLink] = React.useState(initialData.link || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Update states when initialData changes
  React.useEffect(() => {
    setLink(initialData.link || '');
    
    // Handle both old single badge format and new multiple badges format
    if (initialData.badges && Array.isArray(initialData.badges)) {
      // New format: multiple badges - ensure each badge has a flagColor
      setSelectedBadges(initialData.badges.map(badge => ({
        ...badge,
        flagColor: badge.flagColor || '#4caf50' // Default green color
      })));
    } else if (initialData.id) {
      // Old format: single badge - convert to array
      setSelectedBadges([{
        id: initialData.id,
        name: initialData.name,
        description: initialData.description,
        imageUrl: initialData.imageUrl,
        link: initialData.link,
        flagColor: initialData.flagColor || '#4caf50' // Default green color
      }]);
    } else {
      // No badges selected
      setSelectedBadges([]);
    }
  }, [initialData]);

  // API-Aufruf für Moodle Badges
  React.useEffect(() => {
    if (isOpen) {
      fetchBadges();
    }
  }, [isOpen]);

  // Filter badges based on search term
  React.useEffect(() => {
    if (searchTerm === '') {
      setFilteredBadges(badges);
    } else {
      const filtered = badges.filter(badge => 
        badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (badge.description && badge.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredBadges(filtered);
    }
  }, [searchTerm, badges]);

  const fetchBadges = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${config.API_HOST}:${config.API_PORT}/api/moodle/badges`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.API_KEY
        }
      });
      
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.badges) {
        setBadges(data.badges);
        setFilteredBadges(data.badges);
      } else {
        throw new Error(`Failed to fetch badges: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (selectedBadges.length > 0 && link.trim()) {
      onSave({
        // For backwards compatibility, keep the old single badge format if only one badge is selected
        id: selectedBadges.length === 1 ? selectedBadges[0].id : '',
        name: selectedBadges.length === 1 ? selectedBadges[0].name : `${selectedBadges.length} Badges ausgewählt`,
        description: selectedBadges.length === 1 ? selectedBadges[0].description : '',
        imageUrl: selectedBadges.length === 1 ? selectedBadges[0].imageUrl : '',
        link: link.trim(),
        // New format: array of all selected badges
        badges: selectedBadges.map(badge => ({
          id: badge.id,
          name: badge.name,
          description: badge.description,
          imageUrl: badge.imageUrl,
          flagColor: badge.flagColor
        }))
      });
      onClose();
    }
  };

  const handleBadgeSelect = (badge) => {
    // Check if badge is already selected
    const isAlreadySelected = selectedBadges.some(selected => selected.id === badge.id);
    
    if (!isAlreadySelected) {
      setSelectedBadges(prev => [...prev, {
        ...badge,
        flagColor: '#4caf50' // Default green color for new badges
      }]);
    }
  };

  const handleBadgeRemove = (badgeId) => {
    setSelectedBadges(prev => prev.filter(badge => badge.id !== badgeId));
  };

  const handleClearAllBadges = () => {
    setSelectedBadges([]);
  };

  const handleFlagColorChange = (badgeId, newColor) => {
    setSelectedBadges(prev => prev.map(badge => 
      badge.id === badgeId 
        ? { ...badge, flagColor: newColor }
        : badge
    ));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        {/* Badge Editor Dialog */}
        <div 
          style={{ 
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            border: '1px solid #ddd',
            width: '900px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={e => e.stopPropagation()}
        >
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
        Moodle Badges auswählen
      </h3>
      
      {/* Link Eingabefeld */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Link:
          </label>
          <input
            type="url"
            value={link}
            onChange={e => setLink(e.target.value)}
            placeholder="https://example.com/link"
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              flex: 1, 
              minWidth: '300px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={e => e.target.style.borderColor = '#4caf50'}
            onBlur={e => e.target.style.borderColor = '#ccc'}
          />
        </div>
        {!link.trim() && (
          <div style={{ 
            fontSize: '12px', 
            color: '#f44336', 
            marginTop: '4px',
            marginLeft: '92px'
          }}>
            Link ist obligatorisch
          </div>
        )}
      </div>
      
      {/* Suchfeld */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative' }}>
          <FiSearch 
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '16px'
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Badge suchen..."
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px 8px 40px', 
              width: '100%',
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box'
            }}
            onFocus={e => e.target.style.borderColor = '#4caf50'}
            onBlur={e => e.target.style.borderColor = '#ccc'}
          />
        </div>
      </div>

      {/* Loading Anzeige */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          color: '#666',
          fontSize: '14px'
        }}>
          Lade Badges...
        </div>
      )}

      {/* Error Anzeige */}
      {error && (
        <div style={{ 
          padding: '12px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          Fehler beim Laden der Badges: {error}
        </div>
      )}

      {/* Badge Liste */}
      {!loading && !error && (
        <div style={{ 
          maxHeight: '400px', 
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          {filteredBadges.length === 0 ? (
            <div style={{ 
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              {searchTerm ? 'Keine Badges gefunden.' : 'Keine Badges verfügbar.'}
            </div>
          ) : (
            filteredBadges.map(badge => {
              const isSelected = selectedBadges.some(selected => selected.id === badge.id);
              return (
                <div
                  key={badge.id}
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e8f5e8' : 'transparent',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    opacity: isSelected ? 0.7 : 1
                  }}
                  onClick={() => handleBadgeSelect(badge)}
                  onMouseEnter={e => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Badge Bild */}
                  {badge.imageUrl && (
                    <img 
                      src={badge.imageUrl}
                      alt={badge.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                        border: '1px solid #ddd'
                      }}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  
                  {/* Badge Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '500',
                      fontSize: '14px',
                      color: '#333',
                      marginBottom: '4px'
                    }}>
                      {badge.name}
                    </div>
                    {badge.description && (
                      <div style={{ 
                        fontSize: '12px',
                        color: '#666',
                        lineHeight: '1.3',
                        maxHeight: '40px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {badge.description.length > 100 
                          ? `${badge.description.substring(0, 100)}...` 
                          : badge.description
                        }
                      </div>
                    )}
                  </div>

                  {/* Auswahl Indikator */}
                  {isSelected ? (
                    <FiCheck 
                      style={{
                        color: '#4caf50',
                        fontSize: '18px'
                      }}
                    />
                  ) : (
                    <FiPlus 
                      style={{
                        color: '#666',
                        fontSize: '18px'
                      }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Ausgewählte Badges Anzeige */}
      {selectedBadges.length > 0 && (
        <div style={{
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#333'
            }}>
              Ausgewählte Badges ({selectedBadges.length}):
            </div>
            <button
              style={{
                cursor: 'pointer',
                color: '#dc3545',
                backgroundColor: 'transparent',
                border: '1px solid #dc3545',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onClick={handleClearAllBadges}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#dc3545';
                e.target.style.color = 'white';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#dc3545';
              }}
              title="Alle Badges entfernen"
            >
              Alle löschen
            </button>
          </div>
          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #e9ecef',
            borderRadius: '6px',
            backgroundColor: '#f8f9fa'
          }}>
            {selectedBadges.map(badge => (
              <div
                key={badge.id}
                style={{
                  padding: '8px 12px',
                  borderBottom: selectedBadges[selectedBadges.length - 1].id !== badge.id ? '1px solid #e9ecef' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {/* Badge Bild */}
                {badge.imageUrl && (
                  <img 
                    src={badge.imageUrl}
                    alt={badge.name}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '3px',
                      objectFit: 'cover',
                      border: '1px solid #ddd'
                    }}
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                
                {/* Badge Name */}
                <div style={{ 
                  flex: 1,
                  fontSize: '13px',
                  color: '#333',
                  fontWeight: '500'
                }}>
                  {badge.name}
                </div>
                
                {/* Color Picker */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <label 
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Flagfarbe wählen"
                  >
                    Farbe:
                    <input
                      type="color"
                      value={badge.flagColor || '#4caf50'}
                      onChange={(e) => handleFlagColorChange(badge.id, e.target.value)}
                      style={{
                        width: '24px',
                        height: '20px',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        padding: '0'
                      }}
                    />
                  </label>
                </div>
                
                {/* Löschen Button */}
                <button
                  style={{
                    cursor: 'pointer',
                    color: '#dc3545',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '4px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBadgeRemove(badge.id);
                  }}
                  onMouseEnter={e => e.target.style.backgroundColor = '#ffebee'}
                  onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                  title="Badge entfernen"
                >
                  <FiTrash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
        <button
          style={{ 
            cursor: (selectedBadges.length > 0 && link.trim()) ? 'pointer' : 'not-allowed',
            color: 'white',
            backgroundColor: (selectedBadges.length > 0 && link.trim()) ? '#4caf50' : '#ccc',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={handleSave}
          disabled={!(selectedBadges.length > 0 && link.trim())}
          onMouseEnter={e => {
            if (selectedBadges.length > 0 && link.trim()) {
              e.target.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (selectedBadges.length > 0 && link.trim()) {
              e.target.style.backgroundColor = '#4caf50';
            }
          }}
        >
          <FiCheck />
          Speichern
        </button>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#f44336',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={onClose}
          onMouseEnter={e => e.target.style.backgroundColor = '#da190b'}
          onMouseLeave={e => e.target.style.backgroundColor = '#f44336'}
        >
          <FiX />
          Abbrechen
        </button>
      </div>
        </div>
      </div>
    </>
  );
}
